import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Building2,
  Flag,
  Megaphone,
  Settings,
  LogOut,
  Menu,
  Bell,
  ChevronRight,
  Shield,
  CreditCard,
  KeyRound,
  Bot,
  Workflow,
  Handshake,
  Smartphone,
  ShieldAlert,
  Server,
  BarChart3,
  BellRing,
  TrendingUp,
  Inbox,
  Target,
  Cpu,
} from 'lucide-react';

const LOGO_URL = '/logo-stockara.png';

const adminMenuGroups = [
  {
    label: null,
    items: [
      { id: 'overview', label: 'Ringkasan Internal', icon: LayoutDashboard, path: '/admin' },
    ],
  },
  {
    label: 'Tenant & Billing',
    items: [
      { id: 'tenants', label: 'Customer & Tenant', icon: Building2, path: '/admin/tenants' },
      { id: 'analytics', label: 'Analytics', icon: BarChart3, path: '/admin/analytics' },
      { id: 'at-risk', label: 'Tenant At-Risk', icon: ShieldAlert, path: '/admin/at-risk' },
      { id: 'payments', label: 'Pembayaran', icon: CreditCard, path: '/admin/payments' },
      { id: 'payment-settings', label: 'Pengaturan Duitku', icon: Settings, path: '/admin/payment-settings' },
    ],
  },
  {
    label: 'WhatsApp & AI',
    items: [
      { id: 'whatsapp', label: 'WhatsApp & AI', icon: Smartphone, path: '/admin/whatsapp' },
      { id: 'wa-inbox', label: 'WA Inbox Prospek', icon: Inbox, path: '/admin/wa-inbox' },
      { id: 'integration-worker', label: 'Integrasi Channel', icon: Workflow, path: '/admin/integration-worker' },
      { id: 'password-resets', label: 'Bantuan Password', icon: KeyRound, path: '/admin/password-resets' },
    ],
  },
  {
    label: 'Marketing & Growth',
    items: [
      { id: 'affiliates', label: 'Manajemen Affiliate', icon: Handshake, path: '/admin/affiliates' },
      { id: 'affiliate-stats', label: 'Statistik Afiliasi', icon: BarChart3, path: '/admin/affiliate-stats' },
      { id: 'meta-ads', label: 'Statistik Iklan Meta', icon: Target, path: '/admin/meta-ads' },
      { id: 'marketing', label: 'Marketing Campaign', icon: TrendingUp, path: '/admin/marketing' },
      { id: 'broadcast', label: 'Broadcast Customer', icon: Megaphone, path: '/admin/broadcast' },
      { id: 'announcements', label: 'Pengumuman', icon: BellRing, path: '/admin/announcements' },
    ],
  },
  {
    label: 'Sistem',
    items: [
      { id: 'feature-flags', label: 'Akses Fitur', icon: Flag, path: '/admin/feature-flags' },
      { id: 'ai-models', label: 'Model AI', icon: Cpu, path: '/admin/ai-models' },
      { id: 'server-health', label: 'Server Health', icon: Server, path: '/admin/server-health' },
    ],
  },
];

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#EAE5DF]">
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 flex flex-col transition-all duration-300 ease-in-out bg-[#F8F5EF] border-r border-emerald-900/10 text-[#0B1F17] ${sidebarOpen ? 'w-72' : 'w-20'} ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        {/* Logo */}
        <div className="flex items-center h-16 px-4 border-b border-emerald-900/10">
          {sidebarOpen ? (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#E4F5EE] border border-emerald-600/25 rounded-lg flex items-center justify-center overflow-hidden">
                <img src={LOGO_URL} alt="Stockara" className="w-6 h-6 object-contain" />
              </div>
              <div>
                <p className="text-sm font-bold text-[#0B1F17]">Stockara Admin</p>
                <p className="text-xs text-emerald-700">Master Admin</p>
              </div>
            </div>
          ) : (
            <div className="w-full flex justify-center">
              <div className="w-10 h-10 bg-[#E4F5EE] border border-emerald-600/25 rounded-lg flex items-center justify-center">
                <img src={LOGO_URL} alt="Stockara" className="w-6 h-6 object-contain" />
              </div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {adminMenuGroups.map((group, gi) => (
            <div key={group.label || `group-${gi}`} className={gi > 0 ? 'mt-4' : ''}>
              {group.label && sidebarOpen && (
                <p className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {group.label}
                </p>
              )}
              {group.label && !sidebarOpen && gi > 0 && (
                <div className="mx-3 mb-2 border-t border-emerald-900/10" />
              )}
              {group.items.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium mb-1 transition-all duration-200 ${
                    isActive(item.path)
                      ? 'bg-[#DDF3EA] text-emerald-900 border border-emerald-600/25'
                      : 'text-slate-600 hover:bg-[#EEF8F3] hover:text-emerald-900'
                  }`}
                >
                  <item.icon size={20} className="flex-shrink-0" />
                  {sidebarOpen && (
                    <span className="flex-1">{item.label}</span>
                  )}
                  {sidebarOpen && isActive(item.path) && (
                    <ChevronRight size={14} className="text-emerald-700" />
                  )}
                </Link>
              ))}
            </div>
          ))}
          {sidebarOpen && (
            <div className="mt-5 rounded-xl border border-emerald-900/10 bg-white/70 p-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-emerald-900">
                <Workflow size={14} />
                Integration Worker
              </div>
              <p className="mt-2 text-xs leading-relaxed text-slate-600">
                Kelola koneksi customer, status sinkronisasi, dan pekerjaan integrasi dari satu tempat.
              </p>
              <div className="mt-3 flex items-center gap-2 text-xs text-emerald-700">
                <Bot size={14} />
                AI & WhatsApp siap dipantau
              </div>
            </div>
          )}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-emerald-900/10 space-y-2">
          {sidebarOpen && (
            <div className="px-3 py-2 bg-[#E4F5EE] border border-emerald-600/20 rounded-lg">
              <div className="flex items-center gap-2">
                <Shield size={14} className="text-emerald-700" />
                <span className="text-xs text-emerald-700 font-medium">Tim Stockara</span>
              </div>
              {user?.email && (
                <p className="text-xs text-slate-500 mt-1 truncate">{user.email}</p>
              )}
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-xs text-slate-500 hover:text-emerald-900 hover:bg-[#EEF8F3] transition-all"
          >
            <Menu size={16} />
            {sidebarOpen && <span>Tutup Sidebar</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-16 bg-[#F8F5EF] border-b border-emerald-900/10 flex items-center justify-between px-4 lg:px-6 flex-shrink-0">
          <div className="flex items-center gap-4">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 text-slate-600 hover:text-[#0B1F17]">
              <Menu size={20} />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-xs text-slate-600">Ruang kerja internal</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button className="relative p-2 text-slate-600 hover:text-[#0B1F17] hover:bg-[#EEF8F3] rounded-lg transition-all">
              <Bell size={20} />
            </button>
            <div className="flex items-center gap-3 pl-3 border-l border-emerald-900/10">
              <div className="w-8 h-8 bg-emerald-700 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                {user?.name?.charAt(0) || 'D'}
              </div>
              <div className="hidden md:block">
                <p className="text-sm font-medium text-[#0B1F17]">{user?.name || 'Developer'}</p>
                <p className="text-xs text-emerald-700">Master Admin</p>
              </div>
              <button
                onClick={logout}
                className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-all"
                title="Logout"
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="stockara-admin-main flex-1 overflow-y-auto p-4 lg:p-6 bg-[#EAE5DF] bg-[linear-gradient(rgba(26,87,51,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(26,87,51,0.05)_1px,transparent_1px)] bg-[size:32px_32px]">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
